import { IAxToastOptions, ComponentType } from "./ax-toast-type";
import AxToastsController from "./ax-toast-manager";

let toastsController: AxToastsController;
function gettoastsController() {
  if (!toastsController) {
    toastsController = new AxToastsController();
  }
  return toastsController;
}
toastsController = gettoastsController();

export default class AxToast {
  public toastElement: HTMLDivElement;
  private closeIconButton: HTMLSpanElement;

  private options: IAxToastOptions = {
    title: "",
    bodyContent: "", // String or HTML
    className: "",
    componentType: ComponentType.info,
    id: "",
    zIndex: 19999,
    autoDismissAfter: 0, // by default
  };

  public constructor(optionsParam: IAxToastOptions) {
    this.options = optionsParam;
    const componentType = this.options.componentType || ComponentType.info;

    const toast = this.makeToastElement("ax-toast");
    toast.className = `ax-toast ax-toast__${componentType}`;
    this.toastElement = toast;

    // Set toast and variant classes and ID
    if (this.options.id) {
      this.toastElement.setAttribute("id", this.options.id);
    }
    if (this.options.className) {
      this.toastElement.classList.add(this.options.className);
    }
    toast.style.zIndex = !!this.options.zIndex
      ? this.options.zIndex.toString()
      : "19999";

    // Create icon
    const toastTypeName = this.getToastType(componentType); // Toast style
    const axIconTypeName = this.getAxIconType(componentType); // Top left info icon
    const toastIcon = this.makeToastIconElement(toast);
    toastIcon.className = `icon icon__${toastTypeName} ax-icon ax-icon--${axIconTypeName}`;

    // Create message
    const toastMessage = this.makeToastMessageElement(toast);
    toastMessage.className = `message message__${componentType}`;
    toastMessage.innerHTML = this.makeInnerHtml(componentType);

    // Add toast close button
    this.closeIconButton = this.makeToastCloseButtonElement(this.toastElement);
    if (this.options.componentType !== ComponentType.success) {
      this.closeIconButton.className = `cross cross__${componentType} ax-icon ax-icon--clear`;
      this.closeIconButton.onclick = () => this.close();
    } else {
      this.closeIconButton = document.createElement("span");
      this.closeIconButton.className = `cross cross__${componentType}`;
    }

    if (
      typeof this.options.bodyContent !== "string" &&
      this.options.bodyContent !== undefined
    ) {
      this.options.bodyContent.style.margin = "0";
      this.options.bodyContent.style.padding = "0";
    }
    if (!this.options.autoDismissAfter) {
      this.options.autoDismissAfter =
        this.options.componentType === ComponentType.success ? 1500 : 0;
    }

    if (
      this.options.componentType === ComponentType.success &&
      this.options.autoDismissAfter > 0
    ) {
      this.closeIconButton.classList.add("cross__close");
    }

    this.toastElement.appendChild(toastIcon);
    this.toastElement.appendChild(toastMessage);
    this.toastElement.appendChild(this.closeIconButton);
  }

  public static showMessage(message: string, type?: string) {
    const option: IAxToastOptions = {
      bodyContent: message,
      componentType: type,
    };
    switch (type) {
      case undefined:
        option.componentType = ComponentType.info;
        break;

      default:
        option.componentType = type;
        break;
    }
    AxToast.show(option);
  }

  public static showAll(options: IAxToastOptions[]) {
    for (const option of options) {
      AxToast.show(option);
    }
  }

  public static error(option: IAxToastOptions) {
    option.componentType = ComponentType.error;
    this.show(option);
  }

  public static warning(option: IAxToastOptions) {
    option.componentType = ComponentType.warning;
    AxToast.show(option);
  }

  public static success(option: IAxToastOptions) {
    option.componentType = ComponentType.success;
    option.autoDismissAfter =
      option.autoDismissAfter !== undefined && option.autoDismissAfter > 0
        ? option.autoDismissAfter
        : 1500;
    AxToast.show(option);
  }

  public static info(option: IAxToastOptions) {
    option.componentType = ComponentType.info;
    AxToast.show(option);
  }

  public static showErrorMessage(message: string) {
    AxToast.showMessage(message, ComponentType.error);
  }

  public static showWarningMessage(message: string) {
    AxToast.showMessage(message, ComponentType.warning);
  }

  public static showSuccessMessage(message: string) {
    this.showMessage(message, ComponentType.success);
  }

  public static showInfoMessage(message: string) {
    AxToast.showMessage(message, ComponentType.info);
  }
  public static show(option: IAxToastOptions) {
    setTimeout(() => {
      toastsController.show(option);
    }, 500);
  }

  public show(option: IAxToastOptions) {
    AxToast.show(option);
  }

  public showMessage(message: string, type?: string) {
    AxToast.showMessage(message, type);
  }

  public close() {
    toastsController.close(this.toastElement);
  }

  public getCloseIconButton(): HTMLSpanElement {
    return this.closeIconButton;
  }

  public getOptions(): IAxToastOptions {
    return this.options;
  }

  public getToastsController() {
    return toastsController;
  }

  private getToastType(componentType: string): string {
    let iconType: string;
    switch (componentType) {
      case ComponentType.info:
        iconType = "info";
        break;
      case ComponentType.success:
        iconType = "success";
        break;
      case ComponentType.warning:
        iconType = "warning";
        break;
      case ComponentType.error:
        iconType = "error";
        break;
      default:
        iconType = "info";
        break;
    }
    return iconType;
  }

  private getAxIconType(componentType: string): string {
    let iconType: string;
    switch (componentType) {
      case ComponentType.info:
        iconType = "status-info";
        break;
      case ComponentType.success:
        iconType = "status-tick";
        break;
      case ComponentType.warning:
        iconType = "status-exclamation";
        break;
      case ComponentType.error:
        iconType = "status-exclamation";
        break;
      default:
        iconType = "status-info";
        break;
    }
    return iconType;
  }

  private makeTitleText(componentType: string): string {
    if (this.options.title) {
      return this.options.title.toUpperCase() + ": ";
    }

    if (componentType !== undefined && componentType !== ComponentType.info) {
      return componentType.toUpperCase() + ": ";
    }

    return "";
  }

  private makeInnerHtml(componentType: string): string {
    let innerHtmlText: string;
    const titleText = this.makeTitleText(componentType);

    // Attach content
    if (typeof this.options.bodyContent === "string") {
      innerHtmlText = titleText + this.options.bodyContent;
    } else {
      if (this.options.bodyContent !== undefined) {
        this.options.bodyContent.style.margin = "0";
        this.options.bodyContent.style.padding = "0";
      }
      innerHtmlText = titleText + this.options.bodyContent?.innerHTML;
    }
    return innerHtmlText;
  }

  private makeToastElement(styleClassName: string): HTMLDivElement {
    const toastElement: HTMLDivElement = document.createElement("div");
    toastElement.classList.add(styleClassName);
    return toastElement;
  }

  private makeToastIconElement(toast: HTMLDivElement): HTMLSpanElement {
    const iconElement: HTMLSpanElement = document.createElement("span");
    iconElement.classList.add("icon");

    return iconElement;
  }

  private makeToastMessageElement(toast: HTMLDivElement): HTMLDivElement {
    const messageElement: HTMLDivElement = document.createElement("div");
    messageElement.classList.add("message");
    return messageElement;
  }

  private makeToastCloseButtonElement(toast: HTMLDivElement): HTMLSpanElement {
    const el: HTMLSpanElement = document.createElement("span");
    el.classList.add("cross");
    return el;
  }
}
